import React from "react";
import axios from "axios";
import { format, formatDistanceToNow } from "date-fns";

import Loading from "../../../components/common/loading";
import RowList from "../../../components/common/rowList";

const styles = {
  wrap: {
    background: "#ffffff",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    padding: "24px",
  },
  header: {
    marginBottom: "20px",
  },
  title: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 600,
    color: "#1f2d3d",
  },
  description: {
    marginTop: "8px",
    color: "#607284",
    fontSize: "14px",
    lineHeight: 1.6,
  },
  banner: {
    marginBottom: "16px",
    padding: "12px 16px",
    background: "#f1f5f9",
    borderRadius: 6,
    color: "#475569",
    fontSize: "13px",
  },
};

export class DeviceFirmwareStatusPanel extends React.Component {
  state = {
    loading: false,
    polling: false,
    error: null,
  };

  pollingInterval = null;

  componentDidMount = () => {
    const { fwStatus, fwStatusLoaded } = this.getFirmwareResourceState();
    if (fwStatusLoaded && fwStatus?.updateStatus === "inProgress") {
      this.startPolling();
    }
  };

  componentDidUpdate = () => {
    const { fwStatus, fwStatusLoaded } = this.getFirmwareResourceState();
    if (
      fwStatusLoaded &&
      fwStatus?.updateStatus === "inProgress" &&
      !this.state.polling
    ) {
      this.startPolling();
    }
  };

  componentWillUnmount = () => {
    this.stopPolling();
  };

  getFirmwareResourceState = () => {
    const fwStatuses = this.props.resources?.fwStatus;
    const foundFwStatus = fwStatuses?.find(
      (item) =>
        `${item?.enclosureSerialNumber}` ===
        `${this.props.device?.enclosureSerialNumber}`,
    );

    return {
      foundFwStatus,
      fwStatus: foundFwStatus?.fwStatus,
      fwStatusLoading: Boolean(foundFwStatus?.loading),
      fwStatusLoaded: Boolean(foundFwStatus?.loaded),
      lastUpdated: foundFwStatus?.lastUpdated,
    };
  };

  startPolling = () => {
    if (this.state.polling) {
      return;
    }

    this.setState({ polling: true });
    this.props.fetchFwStatus?.(this.props.device?.enclosureSerialNumber);
    this.pollingInterval = setInterval(() => {
      this.props.fetchFwStatus?.(this.props.device?.enclosureSerialNumber);
    }, 12000);
  };

  stopPolling = () => {
    this.setState({ polling: false });
    clearInterval(this.pollingInterval);
  };

  initiateFirmwareUpdate = async (fwVersion) => {
    this.setState({
      loading: true,
      error: null,
    });

    try {
      const response = await axios.post(
        `/v1/devices/${this.props.device?.enclosureSerialNumber}/fwUpdate`,
        { firmwareVersion: fwVersion },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        },
      );

      if (response?.status === 200) {
        this.startPolling();
      } else {
        this.setState({
          error: "Unable to initiate firmware update (non-200).",
        });
      }
    } catch (error) {
      console.error(error);
      const errMessage = error?.response?.data?.message;
      this.setState({
        error:
          errMessage ||
          "Unable to initiate firmware update. Check console for details.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const { device, deviceLoaded } = this.props;
    const { fwStatus, fwStatusLoading, fwStatusLoaded } =
      this.getFirmwareResourceState();
    const { loading, error } = this.state;

    const currentFwVersion = fwStatus?.currentFwVersion;
    const latestFwVersion = fwStatus?.latestFwVersion;
    const availableUpdate = fwStatus?.availableUpdate;
    const updateStatus = fwStatus?.updateStatus;
    const updatePendingFirmware = fwStatus?.updatePendingFirmware;
    const updateInitiatedTime = fwStatus?.updateInitiatedTime;
    const secondsSinceInitiated = fwStatus?.secondsSinceInitiated;
    const rawApiVersion = device?.apiVersion;
    const normalizedApiVersion =
      rawApiVersion === undefined || rawApiVersion === null
        ? ""
        : String(rawApiVersion).trim();
    const deviceInfoLoaded =
      deviceLoaded === undefined ? true : deviceLoaded;
    const skeletonLine = (
      <div
        className="skeleton-blank"
        style={{ width: "120px", height: "14px", margin: 0, padding: 0 }}
      />
    );
    const apiVersionDisplay = normalizedApiVersion
      ? normalizedApiVersion
      : deviceInfoLoaded
        ? "1.0"
        : skeletonLine;

    return (
      <div>
        {fwStatusLoading && !fwStatusLoaded ? (
          <RowList
            rows={[
              { label: "Current Firmware", value: skeletonLine },
              { label: "API Version", value: apiVersionDisplay },
            ]}
          />
        ) : null}

        {error ? (
          <div style={{ color: "maroon", marginBottom: "20px" }}>{error}</div>
        ) : null}

        {fwStatus ? (
          <>
            <RowList
              rows={[
                {
                  label: "Current Firmware",
                  value: (
                    <div style={{ display: "flex" }}>
                      {currentFwVersion}{" "}
                      {currentFwVersion === latestFwVersion && "(Up to date)"}
                      {fwStatusLoading ? (
                        <Loading black />
                      ) : (
                        <span
                          style={{
                            marginLeft: "10px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                          onClick={() =>
                            this.props.fetchFwStatus?.(
                              device?.enclosureSerialNumber,
                            )
                          }
                        >
                          <i className="material-icons">refresh</i>
                        </span>
                      )}
                    </div>
                  ),
                },
                {
                  label: "API Version",
                  value: apiVersionDisplay,
                },
                availableUpdate && updateStatus !== "inProgress"
                  ? {
                      label: "Available Firmware Update",
                      value: (
                        <div>
                          <button
                            className="button"
                            style={{ margin: "0px" }}
                            onClick={() =>
                              this.initiateFirmwareUpdate(latestFwVersion)
                            }
                            disabled={loading}
                          >
                            {loading ? "Updating..." : "Firmware Update Device"}
                          </button>
                        </div>
                      ),
                    }
                  : null,
              ].filter(Boolean)}
            />

            {updateInitiatedTime ? (
              <div
                style={{
                  backgroundColor: "rgb(236 239 243)",
                  padding: "15px",
                  border: "solid #ccc 1px",
                  borderRadius: "3px",
                  fontSize: "90%",
                  marginTop: "10px",
                }}
              >
                {updateInitiatedTime && updateStatus === "inProgress" ? (
                  <div>
                    <div style={{ fontSize: "110%" }}>Update History</div>
                    <div
                      style={{
                        color: "#777",
                        fontSize: "91%",
                      }}
                    >
                      {format(new Date(updateInitiatedTime), "MMM d, HH:mm ")} -
                      Initiated{" "}
                      {formatDistanceToNow(new Date(updateInitiatedTime))} ago
                    </div>
                    <div style={{ marginTop: "10px" }}>
                      Firmware update to version {updatePendingFirmware} in
                      progress...
                    </div>
                  </div>
                ) : null}

                {updateInitiatedTime &&
                (updateStatus === "success" || updateStatus === "failed") ? (
                  <div>
                    <div style={{ fontSize: "110%" }}>Update History</div>
                    <div
                      style={{
                        color: "#777",
                        fontSize: "91%",
                      }}
                    >
                      {format(new Date(updateInitiatedTime), "MMM d, HH:mm ")} -
                      Initiated{" "}
                      {formatDistanceToNow(new Date(updateInitiatedTime))} ago
                    </div>
                    <div style={{ marginTop: "10px" }}>
                      {secondsSinceInitiated > 30 * 60 ? (
                        <>
                          Firmware update was initiated on{" "}
                          {format(new Date(updateInitiatedTime), "MMM d, HH:mm ")}{" "}
                          to version {updatePendingFirmware}
                        </>
                      ) : (
                        <>
                          Firmware update was issued{" "}
                          {updateStatus === "success"
                            ? "successfully"
                            : updateStatus === "failed"
                              ? "unsuccessfully"
                              : ""}{" "}
                          on{" "}
                          {format(new Date(updateInitiatedTime), "MMM d, HH:mm ")}{" "}
                          to version {updatePendingFirmware}
                        </>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    );
  }
}

export default function DeviceConfigurationFirmware({
  section,
  device,
  resources,
  fetchFwStatus,
  deviceLoaded,
  effectivePermissions,
}) {
  const permissionsReady = effectivePermissions?.ready ?? true;
  const canViewFirmware = permissionsReady
    ? effectivePermissions?.canViewFirmware !== false
    : false;

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <h4 style={styles.title}>{section?.label || "Firmware"}</h4>
        {section?.description ? (
          <p style={styles.description}>{section.description}</p>
        ) : null}
      </div>

      {!permissionsReady ? (
        <div style={styles.banner}>Checking access permissions…</div>
      ) : !canViewFirmware ? (
        <div style={styles.banner}>
          You do not have permission to view firmware information for this
          device.
        </div>
      ) : (
        <DeviceFirmwareStatusPanel
          device={device}
          resources={resources}
          fetchFwStatus={fetchFwStatus}
          deviceLoaded={deviceLoaded}
        />
      )}
    </div>
  );
}
